import withAuth from "@auth/withAuth";
import { useAuthContext } from "@auth/context/Auth.context";
import { useToast } from "@celebration/react";
import {
  Button,
  Flex,
  Input,
  Modal,
  TimePicker,
  Toggle,
  Tooltip,
} from "@components/index";
import { ValidationError } from "@models/ValidationError";
import { useSafeNavigate } from "@routes/hooks/useSafeNavigate";
import { useProcess } from "@services/process/hooks";
import {
  formatDateLocalToTimeUtc,
  formatTimeUtcToDateLocal,
} from "@utils/datetime";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styles from "./ProcessesForm.module.scss";
import { schema, ZodError } from "./ProcessesForm.validator";

type TypeOtherCreate = Pick<
  Sentinel.Processes.CreateRequest,
  "name" | "displayName" | "critical"
> & Pick<Sentinel.Processes.CreateRequest["plannedTimes"], "isOneShot" | "sharepointFolderName"> & {
  description?: string | null;
  linkDocumentation?: string | null;
};

const ProcessesForm: React.FC = () => {
  const toast = useToast();
  const navigate = useSafeNavigate();
  const { t } = useTranslation();
  const { hasSomeResourceRole } = useAuthContext();
  const canEdit = hasSomeResourceRole("admin", "timeline-manager");

  const { id } = useParams();

  const [others, setOthers] = useState<TypeOtherCreate>({
    isOneShot: false,
    critical: false,
  });
  const [plannedExecutionStart, setPlannedExecutionStart] =
    useState<Date>(null);
  const [plannedExecutionEnd, setPlannedExecutionEnd] = useState<Date>(null);
  const [tolerancePeriod, setTolerancePeriod] = useState<Date>(null);
  const [showOneShotModal, setShowOneShotModal] = useState(false);
  const [pendingOneShotValue, setPendingOneShotValue] =
    useState<boolean>(false);

  const { editProcess, createProcess, getProcessById } = useProcess();

  useEffect(() => {
    if (id) {
      getProcessById(id)
        .then((result) => {
          setOthers({
            name: result.name,
            displayName: result.displayName,
            sharepointFolderName: result.sharepointFolderName,
            critical: result.critical ?? false,
            isOneShot:
              (result as unknown as { plannedTimes?: { isOneShot?: boolean } })
                .plannedTimes?.isOneShot ??
              result.isOneShot ??
              false,
            description: result.description ?? "",
            linkDocumentation: result.linkDocumentation ?? "",
          });
          
          if (result.tolerancePeriod) {
            const [hours, minutes, seconds] = result.tolerancePeriod.split(":").map(Number);
            const date = new Date();
            date.setHours(hours, minutes, seconds, 0);
            setTolerancePeriod(date);
          }

          if (result.plannedExecutionStart) {
            setPlannedExecutionStart(
              formatTimeUtcToDateLocal(result.plannedExecutionStart)
            );
          }

          if (result.plannedExecutionEnd) {
            setPlannedExecutionEnd(
              formatTimeUtcToDateLocal(result.plannedExecutionEnd)
            );
          }
        })
        .catch((err) => {
          console.error(err);

          toast.openToast({
            title: "Erro",
            description: "Não foi possível carregar os dados para edição!",
            type: "negative",
            autoClose: true,
          });
        });
    }
  }, [getProcessById, id]);

  const handleOneShotChangeCreateMode = (nextValue: boolean) => {
    setOthers((currentForm) => ({
      ...currentForm,
      isOneShot: nextValue,
    }));
  };

  const handleOneShotChangeEditMode = (nextValue: boolean) => {
    setPendingOneShotValue(nextValue);
    setShowOneShotModal(true);
  };

  const confirmOneShotChange = () => {
    setOthers((currentForm) => ({
      ...currentForm,
      isOneShot: pendingOneShotValue,
    }));
    setShowOneShotModal(false);
  };

  const cancelOneShotChange = () => {
    setShowOneShotModal(false);
  };

  const handleSubmit = async () => {
    try {
      const { isOneShot, critical, ...othersWithoutFlags } = others;

      const createRequest: Sentinel.Processes.Request = {
        ...othersWithoutFlags,
        critical: critical === true,
        description: others.description || null,
        linkDocumentation: others.linkDocumentation || null,
        plannedTimes: {
          sharepointFolderName: others.sharepointFolderName,
          plannedExecutionStart: formatDateLocalToTimeUtc(
            plannedExecutionStart
          ),
          plannedExecutionEnd: formatDateLocalToTimeUtc(plannedExecutionEnd),
          isOneShot: isOneShot ?? false,
          tolerancePeriod: tolerancePeriod
            ? tolerancePeriod.toTimeString().slice(0, 8)
            : undefined
        } as Sentinel.Processes.CreateRequest["plannedTimes"],
      };

      schema.parse(createRequest);

      if (id) {
        await editProcess(id, createRequest);
      } else {
        await createProcess(createRequest);
      }

      toast.openToast({
        title: "Sucesso",
        description: "Processo salvo com sucesso!",
        type: "positive",
        autoClose: true,
      });

      return navigate("/processes");
    } catch (err) {
      if (err instanceof ZodError) {
        err.issues.forEach((issue) => {
          toast.openToast({
            title: "Validação",
            description: issue.message,
            type: "warning",
            autoClose: true,
          });
        });
      } else if (err instanceof ValidationError) {
        err.issues.forEach((issue) => {
          toast.openToast({
            title: "Validação",
            description: issue.message,
            type: "warning",
            autoClose: true,
          });
        });
      } else {
        toast.openToast({
          title: "Erro",
          description: "Ocorreu um erro ao realizar a operação",
          type: "negative",
          autoClose: true,
        });
      }
    }
  };

  return (
    <form className={styles.form}>
      <h1 className="text text--xl text--neutral--600 pb-50">
        Cadastro de Processos
      </h1>

      <Input.Text
        label="Nome"
        placeholder="Informe o nome"
        size="lg"
        value={others?.name ?? ""}
        onChange={(event) => {
          setOthers((currentForm) => ({
            ...currentForm,
            name: event.target.value,
          }));
        }}
      />

      <Input.Text
        label="Nome de Exibição"
        placeholder="Informe o nome de exibição"
        size="lg"
        value={others?.displayName ?? ""}
        onChange={(event) => {
          setOthers((currentForm) => ({
            ...currentForm,
            displayName: event.target.value,
          }));
        }}
      />

      <Input.Text
        label="Nome da Pasta do Sharepoint"
        placeholder="Informe o nome da pasta do sharepoint"
        size="lg"
        value={others?.sharepointFolderName ?? ""}
        onChange={(event) => {
          setOthers((currentForm) => ({
            ...currentForm,
            sharepointFolderName: event.target.value,
          }));
        }}
      />

      {canEdit && (
        <div className={styles["textarea-field"]}>
          <label htmlFor="description" className={styles["textarea-label"]}>
            Descrição do Processo
          </label>
          <textarea
            id="description"
            className={styles.textarea}
            placeholder="Informe a descrição do processo"
            rows={5}
            maxLength={250}
            value={others?.description ?? ""}
            onChange={(event) => {
              setOthers((currentForm) => ({
                ...currentForm,
                description: event.target.value,
              }));
            }}
          />
          <div className={styles["char-count"]}>
            {(others?.description ?? "").length}/250
          </div>
        </div>
      )}

      {canEdit && (
        <Input.Text
          label="Link Padrão do Processo"
          placeholder="Informe o link padrão do processo"
          size="lg"
          value={others?.linkDocumentation ?? ""}
          onChange={(event) => {
            setOthers((currentForm) => ({
              ...currentForm,
              linkDocumentation: event.target.value,
            }));
          }}
        />
      )}

      <Flex.Row
        flexWrap="nowrap"
        alignItems="center"
        alignContent="center"
        justifyContent="space-between"
        justifyItems="center"
        gap="10px"
      >
        <TimePicker
          id="plannedExecutionStart"
          label="Inicio Planejado"
          placeholder="Informe o inicio planejado"
          size="lg"
          placement="right"
          value={plannedExecutionStart}
          onChange={setPlannedExecutionStart}
        />

        <TimePicker
          id="plannedExecutionEnd"
          label="Fim Planejado"
          placeholder="Informe o fim planejado"
          size="lg"
          placement="right"
          value={plannedExecutionEnd}
          onChange={setPlannedExecutionEnd}
        />
      </Flex.Row>

      <Flex.Row>
        <TimePicker
          id="tolerancePeriod"
          label="Tolerância"
          placeholder="Informe a tolerância"
          size="lg"
          format="HH:mm:ss"
          placement="right"
          hideHours={hour => hour > 5}
          value={tolerancePeriod}
          onChange={setTolerancePeriod}
        />
      </Flex.Row>

      <Flex.Row
        alignItems="center"
        gap="12px"
        style={{ marginTop: "20px", marginBottom: "20px" }}
      >
        <Tooltip
          title={t("process.oneshot.tooltip.title")}
          description={t("process.oneshot.tooltip.description")}
          position="top"
        >
          <Flex.Row alignItems="center" gap="8px">
            <label
              style={{
                cursor: "pointer",
                userSelect: "none",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Toggle
                id="isOneShot"
                checked={others?.isOneShot ?? false}
                onChange={
                  id
                    ? handleOneShotChangeEditMode
                    : handleOneShotChangeCreateMode
                }
                size="md"
              />
              {t("process.oneshot.label")}
            </label>
          </Flex.Row>
        </Tooltip>
      </Flex.Row>

      <Flex.Row alignItems="center" gap="12px" style={{ marginBottom: "20px" }}>
        <Flex.Row alignItems="center" gap="8px">
          <label
            style={{
              cursor: "pointer",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Toggle
              id="critical"
              checked={others?.critical === true}
              onChange={(nextValue) =>
                setOthers((currentForm) => ({
                  ...currentForm,
                  critical: nextValue,
                }))
              }
              size="md"
            />
            Crítico
          </label>
        </Flex.Row>
      </Flex.Row>

      <Flex justifyContent="end">
        <Button size="md" label="Salvar" onClick={() => void handleSubmit()} />
      </Flex>

      <Modal.Confirmation
        title={t("process.oneshot.modal.title")}
        description={t("process.oneshot.modal.description")}
        onCancel={cancelOneShotChange}
        onConfirm={confirmOneShotChange}
        open={showOneShotModal}
      />
    </form>
  );
};

export default withAuth(ProcessesForm);
